import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import type { PortfolioSelection } from "@/dashboard/inventory_management/common/usePortfolioSelection";
import {
  sendKindAuthority,
  type SendKind,
} from "@/dashboard/inventory_management/send/cnmc/cnmcOutboundRules";
import {
  isHostedRecipient as isHosted,
  isRecipientComplete,
  recipientCountryDisplay,
  type RecipientFields,
  type SendRecipient,
  toRecipientFields,
} from "@/dashboard/inventory_management/send/sendRecipient";
import type { PortfolioItemTransfer } from "@/types";
import type { RegistrationAuthority } from "@/utils/registryAccounts";
import { computed, reactive, ref, watch, type Ref } from "vue";
import { useI18n } from "vue-i18n";

const UNIT_PRICE_PATTERN = /^\d+([.,]\d{1,2})?$/;

/**
 * @property companyId - The company whose GOs are sent
 * @property recipient - Send's recipient, which the registry form edits in full
 */
export interface UseRegistrySendOptions {
  selection: PortfolioSelection<SendKind>;
  companyId: () => number;
  recipient: Ref<SendRecipient>;
}

/**
 * The state and API call of a send through a registry API: the authority the selection executes in,
 * the recipient checks it implies, a TGE send's unit price, and the review before posting the order.
 */
export function useRegistrySend(options: UseRegistrySendOptions) {
  const { selection, companyId, recipient } = options;
  const { t } = useI18n();
  const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();

  const unitPricePln = ref("");
  const isReviewOpen = ref(false);
  const isBusy = ref(false);

  // Nothing selected yet, or a CNMC selection the registry form is hidden for: AIB, the common case.
  const authority = computed(
    (): RegistrationAuthority =>
      sendKindAuthority(selection.selectionKey) ?? "AIB"
  );

  // NOTE: TGE is an island — it only moves certificates PL to PL, so an RGP selection always
  // goes to a TGE member code.
  const isTgeSelection = computed(() => authority.value === "RGP");

  const isHostedRecipient = computed(() => isHosted(recipient.value));

  // NOTE: hosted sends route to Soldera's own TGE account, where the transfer records a
  // placeholder price, so only an external TGE recipient needs a user-entered unit price.
  const requiresUnitPrice = computed(
    () => isTgeSelection.value && !isHostedRecipient.value
  );

  const countryDisplay = computed(() =>
    recipientCountryDisplay(recipient.value, authority.value)
  );

  const isUnitPriceValid = computed(() => {
    const raw = unitPricePln.value.trim();
    return UNIT_PRICE_PATTERN.test(raw) && Number(raw.replace(",", ".")) > 0;
  });

  const showUnitPriceValidationError = computed(
    () => !!unitPricePln.value.trim() && !isUnitPriceValid.value
  );

  const submittableUnitPricePln = computed(() =>
    requiresUnitPrice.value && isUnitPriceValid.value
      ? Number(unitPricePln.value.trim().replace(",", ".")).toFixed(2)
      : undefined
  );

  const isComplete = computed(
    () =>
      isRecipientComplete(recipient.value, authority.value) &&
      (!requiresUnitPrice.value || isUnitPriceValid.value)
  );

  watch(requiresUnitPrice, (required) => {
    if (!required) unitPricePln.value = "";
  });

  const openReview = () => {
    isReviewOpen.value = true;
  };

  /** Posts the send order of the selection; false when it failed (the error handler shows why). */
  const confirm = async (): Promise<boolean> => {
    if (isBusy.value) return false;
    isBusy.value = true;
    try {
      const payload: RecipientFields & {
        portfolio_items: PortfolioItemTransfer[];
        unit_price_pln?: string;
      } = {
        ...toRecipientFields(recipient.value),
        portfolio_items: selection.selectedItemsForApi,
      };
      if (submittableUnitPricePln.value) {
        payload.unit_price_pln = submittableUnitPricePln.value;
      }
      await goSolidApi.post(
        `/api/company/${companyId()}/portfolio/send`,
        payload
      );
      globalSuccessHandler(
        t("common.success"),
        t("dashboard.portfolio.sendGoSuccessMessage")
      );
      return true;
    } catch (error) {
      globalErrorHandler(error);
      return false;
    } finally {
      isBusy.value = false;
    }
  };

  /** Forgets what the last send filed, once Send has cleared its recipient. */
  const reset = () => {
    unitPricePln.value = "";
  };

  return reactive({
    authority,
    isTgeSelection,
    isHostedRecipient,
    requiresUnitPrice,
    countryDisplay,
    unitPricePln,
    showUnitPriceValidationError,
    submittableUnitPricePln,
    isComplete,
    isBusy,
    isReviewOpen,
    openReview,
    confirm,
    reset,
  });
}
